import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { identifyBrevo, touchActivity } from "@/lib/brevo";

interface Props {
  userId: string | null;
  email: string | null;
}

const NameCard = ({ userId, email }: Props) => {
  const { toast } = useToast();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [initial, setInitial] = useState<{ first: string; last: string }>({ first: "", last: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("user_id", userId)
        .maybeSingle();
      if (cancelled) return;
      const first = (data?.first_name as string | null) ?? "";
      const last = (data?.last_name as string | null) ?? "";
      setFirstName(first);
      setLastName(last);
      setInitial({ first, last });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const dirty = firstName.trim() !== initial.first || lastName.trim() !== initial.last;

  const handleSave = async () => {
    if (!userId) return;
    const first = firstName.trim();
    const last = lastName.trim();
    if (!first) {
      toast({ title: "First name required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ first_name: first, last_name: last || null })
      .eq("user_id", userId);
    setSaving(false);
    if (error) {
      toast({ title: "Couldn't save name", description: error.message, variant: "destructive" });
      return;
    }
    setInitial({ first, last });
    toast({ title: "Name updated" });
    // Push refreshed name into Brevo (client identify + server sync).
    if (email) {
      void identifyBrevo({ email, firstName: first, lastName: last || null });
    }
    touchActivity();
  };

  return (
    <div className="bg-card border border-border p-6 w-full space-y-4">
      <div>
        <h3 className="text-foreground font-medium">Your name</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Used to personalize emails and the app.
        </p>
      </div>
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="account-first-name">First name</Label>
              <Input
                id="account-first-name"
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="account-last-name">
                Last name <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="account-last-name"
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
          <Button
            className="rounded-full"
            onClick={handleSave}
            disabled={saving || !dirty || !firstName.trim()}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </>
      )}
    </div>
  );
};

export default NameCard;
